/**
 * AIShield Frontend API Client
 * Relative /api/v1 calls routed through Vite proxy to management API (http://127.0.0.1:8001).
 * Strictly typed, bounded timeouts, AbortSignal support, and zero silent synthetic fallbacks.
 */

import type {
  BundleQueryParams,
  BackendBundleResponse,
  RequestRecord,
  ManagementHealthResponse,
  BackendDecisionAttribution,
  BackendRecordedModes,
  BackendAiSummaryMetrics,
  WebsiteRecord,
  WebsiteListResponse,
  WebsiteCreatePayload,
  CnameInstructions,
} from "../types/telemetry.ts";

export class ApiClientError extends Error {
  public readonly status: number;
  public readonly data?: unknown;
  public readonly retryAfter?: number;

  constructor(message: string, status: number, data?: unknown, retryAfter?: number) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.data = data;
    this.retryAfter = retryAfter;
  }
}

export interface RequestOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
  baseUrl?: string;
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  headers?: Record<string, string>;
  body?: unknown;
}

export interface AccountResponse {
  id: string;
  email: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface LoginPayload {
  email: string;
  password: string;
}

export interface RegisterPayload {
  email: string;
  password: string;
}

export interface LogoutResponse {
  status: string;
}

const DEFAULT_TIMEOUT_MS = 10000;
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Serializes query parameters into URLSearchParams matching backend schema.
 */
export function serializeBundleQueryParams(
  query: BundleQueryParams
): URLSearchParams {
  const params = new URLSearchParams();

  if (query.websiteId) {
    params.set("website_id", query.websiteId);
  }
  if (query.timeRange) {
    params.set("time_range", query.timeRange);
  }
  if (query.shortcut) {
    params.set("shortcut", query.shortcut);
  }
  if (query.decision) {
    params.set("decision", query.decision);
  }
  if (query.inspectionStatus) {
    params.set("inspection_status", query.inspectionStatus);
  }
  if (query.search !== undefined && query.search.trim().length > 0) {
    params.set("search", query.search.trim().slice(0, 100));
  }
  if (query.sortField) {
    params.set("sort_field", query.sortField);
  }
  if (query.sortOrder) {
    params.set("sort_order", query.sortOrder);
  }
  if (query.page !== undefined) {
    params.set("page", String(Math.max(1, query.page)));
  }
  if (query.pageSize !== undefined) {
    params.set("page_size", String(Math.max(1, Math.min(100, query.pageSize))));
  }

  return params;
}

const VALID_GATEWAY_OUTCOMES = new Set([
  "COMPLETED",
  "BLOCKED",
  "GATEWAY_ERROR",
  "REJECTED",
  "CLIENT_DISCONNECT",
  "UPSTREAM_STREAM_ERROR",
  "SERVER_SHUTDOWN",
  "INTERRUPTED_UNKNOWN",
  "LEGACY_UNVERIFIED",
]);

const VALID_INSPECTION_STATUSES = new Set([
  "SUCCESS",
  "NOT_RUN",
  "TIMEOUT",
  "CONNECTION_FAILURE",
  "PARSING_FAILURE",
  "UNEXPECTED_STATUS",
  "MISSING_METADATA",
  "INVALID_METADATA",
  "CONFLICTING_METADATA",
]);

const VALID_POLICY_MODES = new Set(["BLOCKING", "FLAGGING", "MONITORING"]);
const VALID_POLICY_DECISIONS = new Set(["ALLOW", "FLAG", "BLOCK"]);
const VALID_TIME_RANGES = new Set(["15m", "60m", "24h", "all"]);

/**
 * Validates /api/v1/healthz payload.
 */
export function validateManagementHealth(data: unknown): ManagementHealthResponse {
  if (typeof data !== "object" || data === null || Array.isArray(data)) {
    throw new ApiClientError("Malformed health response: expected object", 502, data);
  }
  const obj = data as Record<string, unknown>;
  if (typeof obj.status !== "string" || !obj.status.trim()) {
    throw new ApiClientError("Malformed health response: missing or invalid 'status'", 502, data);
  }
  if (typeof obj.service !== "string" || !obj.service.trim()) {
    throw new ApiClientError("Malformed health response: missing or invalid 'service'", 502, data);
  }
  if (typeof obj.database !== "string" || !obj.database.trim()) {
    throw new ApiClientError("Malformed health response: missing or invalid 'database'", 502, data);
  }
  return {
    status: obj.status,
    service: obj.service,
    database: obj.database,
  };
}

/**
 * Validates RequestRecord payload (both in request details and table items).
 */
export function validateRequestRecord(data: unknown, context = "RequestRecord"): RequestRecord {
  if (typeof data !== "object" || data === null || Array.isArray(data)) {
    throw new ApiClientError(`Malformed ${context}: expected object`, 502, data);
  }
  const obj = data as Record<string, unknown>;

  // Required non-nullable fields
  if (typeof obj.id !== "string" || !UUID_REGEX.test(obj.id.trim())) {
    throw new ApiClientError(`Malformed ${context}: invalid or missing UUID 'id'`, 502, data);
  }

  if (typeof obj.timestamp !== "string" || !obj.timestamp.trim() || isNaN(Date.parse(obj.timestamp))) {
    throw new ApiClientError(`Malformed ${context}: invalid or missing ISO timestamp`, 502, data);
  }

  if (typeof obj.method !== "string" || !obj.method.trim()) {
    throw new ApiClientError(`Malformed ${context}: invalid or missing 'method'`, 502, data);
  }

  if (typeof obj.raw_path !== "string") {
    throw new ApiClientError(`Malformed ${context}: invalid or missing 'raw_path'`, 502, data);
  }

  if (typeof obj.client_ip !== "string" || !obj.client_ip.trim()) {
    throw new ApiClientError(`Malformed ${context}: invalid or missing 'client_ip'`, 502, data);
  }

  if (typeof obj.body_bytes !== "number" || !Number.isInteger(obj.body_bytes) || obj.body_bytes < 0) {
    throw new ApiClientError(`Malformed ${context}: invalid or missing non-negative integer 'body_bytes'`, 502, data);
  }

  if (typeof obj.response_status !== "number" || !Number.isInteger(obj.response_status)) {
    throw new ApiClientError(`Malformed ${context}: invalid or missing integer 'response_status'`, 502, data);
  }

  if (typeof obj.gateway_outcome !== "string" || !VALID_GATEWAY_OUTCOMES.has(obj.gateway_outcome)) {
    throw new ApiClientError(`Malformed ${context}: invalid or unrecognized 'gateway_outcome': '${obj.gateway_outcome}'`, 502, data);
  }

  if (typeof obj.inspection_status !== "string" || !VALID_INSPECTION_STATUSES.has(obj.inspection_status)) {
    throw new ApiClientError(`Malformed ${context}: invalid or unrecognized 'inspection_status': '${obj.inspection_status}'`, 502, data);
  }

  if (typeof obj.latency_ms !== "number" || !Number.isFinite(obj.latency_ms) || obj.latency_ms < 0) {
    throw new ApiClientError(`Malformed ${context}: invalid or missing finite non-negative 'latency_ms'`, 502, data);
  }

  // Headers: validate object and validate that all values are strings
  if (!("headers" in obj) || typeof obj.headers !== "object" || obj.headers === null || Array.isArray(obj.headers)) {
    throw new ApiClientError(`Malformed ${context}: invalid or missing 'headers' object`, 502, data);
  }
  const headersObj: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj.headers)) {
    if (typeof v !== "string") {
      throw new ApiClientError(`Malformed ${context}: header '${k}' value must be a string`, 502, data);
    }
    headersObj[k] = v;
  }

  // Required nullable fields: must be explicitly present in obj
  const requiredNullableFields = [
    "client_request_id",
    "query_string",
    "upstream_status",
    "blocking_score",
    "detection_score",
    "inspection_latency_ms",
    "policy_mode",
    "policy_decision",
    "upstream_latency_ms",
    "error_detail",
  ];
  for (const field of requiredNullableFields) {
    if (!(field in obj) || obj[field] === undefined) {
      throw new ApiClientError(`Malformed ${context}: missing required field '${field}' (explicit null required if empty)`, 502, data);
    }
  }

  // Value validity checks for nullable fields
  if (obj.client_request_id !== null && typeof obj.client_request_id !== "string") {
    throw new ApiClientError(`Malformed ${context}: 'client_request_id' must be null or a string`, 502, data);
  }

  if (obj.query_string !== null && typeof obj.query_string !== "string") {
    throw new ApiClientError(`Malformed ${context}: 'query_string' must be null or a string`, 502, data);
  }

  if (obj.upstream_status !== null && (typeof obj.upstream_status !== "number" || !Number.isInteger(obj.upstream_status))) {
    throw new ApiClientError(`Malformed ${context}: 'upstream_status' must be null or an integer`, 502, data);
  }

  // Scores: non-negative integers without upper limit of 100
  if (obj.blocking_score !== null) {
    if (typeof obj.blocking_score !== "number" || !Number.isInteger(obj.blocking_score) || obj.blocking_score < 0) {
      throw new ApiClientError(`Malformed ${context}: 'blocking_score' must be null or a non-negative integer`, 502, data);
    }
  }

  if (obj.detection_score !== null) {
    if (typeof obj.detection_score !== "number" || !Number.isInteger(obj.detection_score) || obj.detection_score < 0) {
      throw new ApiClientError(`Malformed ${context}: 'detection_score' must be null or a non-negative integer`, 502, data);
    }
  }

  if (obj.inspection_latency_ms !== null) {
    if (typeof obj.inspection_latency_ms !== "number" || !Number.isFinite(obj.inspection_latency_ms) || obj.inspection_latency_ms < 0) {
      throw new ApiClientError(`Malformed ${context}: 'inspection_latency_ms' must be null or a non-negative finite number`, 502, data);
    }
  }

  if (obj.policy_mode !== null) {
    if (typeof obj.policy_mode !== "string" || !VALID_POLICY_MODES.has(obj.policy_mode)) {
      throw new ApiClientError(`Malformed ${context}: invalid 'policy_mode': '${obj.policy_mode}'`, 502, data);
    }
  }

  if (obj.policy_decision !== null) {
    if (typeof obj.policy_decision !== "string" || !VALID_POLICY_DECISIONS.has(obj.policy_decision)) {
      throw new ApiClientError(`Malformed ${context}: invalid 'policy_decision': '${obj.policy_decision}'`, 502, data);
    }
  }

  if (obj.upstream_latency_ms !== null) {
    if (typeof obj.upstream_latency_ms !== "number" || !Number.isFinite(obj.upstream_latency_ms) || obj.upstream_latency_ms < 0) {
      throw new ApiClientError(`Malformed ${context}: 'upstream_latency_ms' must be null or a non-negative finite number`, 502, data);
    }
  }

  if (obj.error_detail !== null && typeof obj.error_detail !== "string") {
    throw new ApiClientError(`Malformed ${context}: 'error_detail' must be null or a string`, 502, data);
  }

  const aiStatus = obj.ai_status !== undefined && obj.ai_status !== null ? String(obj.ai_status) : null;
  const aiPredictedCat = obj.ai_predicted_category !== undefined && obj.ai_predicted_category !== null ? String(obj.ai_predicted_category) : null;
  const aiModelProb = obj.ai_model_probability !== undefined && obj.ai_model_probability !== null ? Number(obj.ai_model_probability) : null;
  const aiClassProbs = obj.ai_class_probabilities !== undefined && obj.ai_class_probabilities !== null && typeof obj.ai_class_probabilities === "object" && !Array.isArray(obj.ai_class_probabilities) ? (obj.ai_class_probabilities as Record<string, number>) : null;
  const aiPreTrunc = obj.ai_preprocessor_truncated !== undefined && obj.ai_preprocessor_truncated !== null ? Boolean(obj.ai_preprocessor_truncated) : null;
  const aiTokTrunc = obj.ai_token_truncated !== undefined && obj.ai_token_truncated !== null ? Boolean(obj.ai_token_truncated) : null;
  const aiLatency = obj.ai_latency_ms !== undefined && obj.ai_latency_ms !== null ? Number(obj.ai_latency_ms) : null;
  const aiModelId = obj.ai_model_identity !== undefined && obj.ai_model_identity !== null ? String(obj.ai_model_identity) : null;
  const aiEncRev = obj.ai_encoder_revision !== undefined && obj.ai_encoder_revision !== null ? String(obj.ai_encoder_revision) : null;
  const aiHeadChk = obj.ai_head_checksum !== undefined && obj.ai_head_checksum !== null ? String(obj.ai_head_checksum) : null;
  const aiPolMode = obj.ai_policy_mode !== undefined && obj.ai_policy_mode !== null ? String(obj.ai_policy_mode) : null;
  const crsPolicyDecision = obj.crs_policy_decision !== undefined && obj.crs_policy_decision !== null ? String(obj.crs_policy_decision) : null;
  const decisionSource = obj.decision_source !== undefined && obj.decision_source !== null ? String(obj.decision_source) : null;
  const decisionReason = obj.decision_reason !== undefined && obj.decision_reason !== null ? String(obj.decision_reason) : null;
  const aiFlagThreshold = obj.ai_flag_threshold !== undefined && obj.ai_flag_threshold !== null ? Number(obj.ai_flag_threshold) : null;
  const aiBlockThreshold = obj.ai_block_threshold !== undefined && obj.ai_block_threshold !== null ? Number(obj.ai_block_threshold) : null;

  return {
    id: obj.id.trim(),
    client_request_id: obj.client_request_id as string | null,
    timestamp: obj.timestamp as string,
    method: obj.method as string,
    raw_path: obj.raw_path as string,
    query_string: obj.query_string as string | null,
    client_ip: obj.client_ip as string,
    headers: headersObj,
    body_bytes: obj.body_bytes as number,
    response_status: obj.response_status as number,
    upstream_status: obj.upstream_status as number | null,
    gateway_outcome: obj.gateway_outcome as any,
    inspection_status: obj.inspection_status as any,
    blocking_score: obj.blocking_score as number | null,
    detection_score: obj.detection_score as number | null,
    inspection_latency_ms: obj.inspection_latency_ms as number | null,
    policy_mode: obj.policy_mode as any,
    policy_decision: obj.policy_decision as any,
    latency_ms: obj.latency_ms as number,
    upstream_latency_ms: obj.upstream_latency_ms as number | null,
    error_detail: obj.error_detail as string | null,
    ai_status: aiStatus,
    ai_predicted_category: aiPredictedCat,
    ai_model_probability: aiModelProb,
    ai_class_probabilities: aiClassProbs,
    ai_preprocessor_truncated: aiPreTrunc,
    ai_token_truncated: aiTokTrunc,
    ai_latency_ms: aiLatency,
    ai_model_identity: aiModelId,
    ai_encoder_revision: aiEncRev,
    ai_head_checksum: aiHeadChk,
    ai_policy_mode: aiPolMode,
    crs_policy_decision: crsPolicyDecision,
    decision_source: decisionSource,
    decision_reason: decisionReason,
    ai_flag_threshold: aiFlagThreshold,
    ai_block_threshold: aiBlockThreshold,
  };
}

/**
 * Validates /api/v1/dashboard-bundle payload.
 */
export function validateDashboardBundle(data: unknown): BackendBundleResponse {
  if (typeof data !== "object" || data === null || Array.isArray(data)) {
    throw new ApiClientError("Malformed bundle response: expected object", 502, data);
  }
  const obj = data as Record<string, unknown>;

  if (typeof obj.server_time !== "string" || !obj.server_time.trim() || isNaN(Date.parse(obj.server_time))) {
    throw new ApiClientError("Malformed bundle response: invalid or missing 'server_time'", 502, data);
  }

  if (typeof obj.window !== "object" || obj.window === null || Array.isArray(obj.window)) {
    throw new ApiClientError("Malformed bundle response: invalid or missing 'window' object", 502, data);
  }
  const win = obj.window as Record<string, unknown>;
  if (typeof win.time_range !== "string" || !VALID_TIME_RANGES.has(win.time_range)) {
    throw new ApiClientError(`Malformed bundle response: invalid window 'time_range': '${win.time_range}'`, 502, data);
  }
  if (typeof win.time_from !== "string" || !win.time_from.trim() || isNaN(Date.parse(win.time_from))) {
    throw new ApiClientError("Malformed bundle response: invalid window 'time_from'", 502, data);
  }
  if (typeof win.time_to !== "string" || !win.time_to.trim() || isNaN(Date.parse(win.time_to))) {
    throw new ApiClientError("Malformed bundle response: invalid window 'time_to'", 502, data);
  }

  if (typeof obj.summary !== "object" || obj.summary === null || Array.isArray(obj.summary)) {
    throw new ApiClientError("Malformed bundle response: invalid or missing 'summary' object", 502, data);
  }
  const sum = obj.summary as Record<string, unknown>;
  const integerFields = [
    "total_requests",
    "allow_count",
    "flag_count",
    "block_count",
    "not_evaluated_count",
    "timeouts_count",
    "failed_count",
  ];
  for (const f of integerFields) {
    if (!(f in sum) || typeof sum[f] !== "number" || !Number.isInteger(sum[f]) || (sum[f] as number) < 0) {
      throw new ApiClientError(`Malformed bundle response: summary.${f} must be a non-negative integer`, 502, data);
    }
  }

  // Nullable summary fields must be explicitly present
  if (!("block_rate" in sum) || sum.block_rate === undefined) {
    throw new ApiClientError("Malformed bundle response: missing required field 'summary.block_rate'", 502, data);
  }
  if (sum.block_rate !== null) {
    if (typeof sum.block_rate !== "number" || !Number.isFinite(sum.block_rate) || sum.block_rate < 0 || sum.block_rate > 100) {
      throw new ApiClientError("Malformed bundle response: summary.block_rate must be null or a percentage between 0 and 100", 502, data);
    }
  }

  if (!("avg_transit_ms" in sum) || sum.avg_transit_ms === undefined) {
    throw new ApiClientError("Malformed bundle response: missing required field 'summary.avg_transit_ms'", 502, data);
  }
  if (sum.avg_transit_ms !== null) {
    if (typeof sum.avg_transit_ms !== "number" || !Number.isFinite(sum.avg_transit_ms) || sum.avg_transit_ms < 0) {
      throw new ApiClientError("Malformed bundle response: summary.avg_transit_ms must be null or a non-negative number", 502, data);
    }
  }

  if (!("avg_upstream_header_ms" in sum) || sum.avg_upstream_header_ms === undefined) {
    throw new ApiClientError("Malformed bundle response: missing required field 'summary.avg_upstream_header_ms'", 502, data);
  }
  if (sum.avg_upstream_header_ms !== null) {
    if (typeof sum.avg_upstream_header_ms !== "number" || !Number.isFinite(sum.avg_upstream_header_ms) || sum.avg_upstream_header_ms < 0) {
      throw new ApiClientError("Malformed bundle response: summary.avg_upstream_header_ms must be null or a non-negative number", 502, data);
    }
  }

  if (typeof obj.sidebar_counts !== "object" || obj.sidebar_counts === null || Array.isArray(obj.sidebar_counts)) {
    throw new ApiClientError("Malformed bundle response: invalid or missing 'sidebar_counts' object", 502, data);
  }
  const side = obj.sidebar_counts as Record<string, unknown>;
  for (const f of ["all", "blocked", "flagged", "failures"]) {
    if (typeof side[f] !== "number" || !Number.isInteger(side[f]) || (side[f] as number) < 0) {
      throw new ApiClientError(`Malformed bundle response: sidebar_counts.${f} must be a non-negative integer`, 502, data);
    }
  }

  if (typeof obj.chart !== "object" || obj.chart === null || Array.isArray(obj.chart)) {
    throw new ApiClientError("Malformed bundle response: invalid or missing 'chart' object", 502, data);
  }
  const chart = obj.chart as Record<string, unknown>;
  if (typeof chart.bucket_seconds !== "number" || !Number.isInteger(chart.bucket_seconds) || chart.bucket_seconds <= 0) {
    throw new ApiClientError("Malformed bundle response: chart.bucket_seconds must be a positive integer", 502, data);
  }
  if (!Array.isArray(chart.buckets)) {
    throw new ApiClientError("Malformed bundle response: chart.buckets must be an array", 502, data);
  }
  for (let i = 0; i < chart.buckets.length; i++) {
    const b = chart.buckets[i];
    if (typeof b !== "object" || b === null || Array.isArray(b)) {
      throw new ApiClientError(`Malformed bundle response: chart.buckets[${i}] must be an object`, 502, data);
    }
    if (typeof b.bucket_key !== "string" || !b.bucket_key.trim()) {
      throw new ApiClientError(`Malformed bundle response: chart.buckets[${i}].bucket_key must be a non-empty string`, 502, data);
    }
    if (typeof b.label !== "string") {
      throw new ApiClientError(`Malformed bundle response: chart.buckets[${i}].label must be a string`, 502, data);
    }
    if (typeof b.start_time !== "number" || !Number.isFinite(b.start_time)) {
      throw new ApiClientError(`Malformed bundle response: chart.buckets[${i}].start_time must be a finite number`, 502, data);
    }
    if (typeof b.end_time !== "number" || !Number.isFinite(b.end_time)) {
      throw new ApiClientError(`Malformed bundle response: chart.buckets[${i}].end_time must be a finite number`, 502, data);
    }
    for (const f of ["allow", "flag", "block", "not_evaluated", "total"]) {
      if (typeof b[f] !== "number" || !Number.isInteger(b[f]) || b[f] < 0) {
        throw new ApiClientError(`Malformed bundle response: chart.buckets[${i}].${f} must be a non-negative integer`, 502, data);
      }
    }
  }

  if (typeof obj.table !== "object" || obj.table === null || Array.isArray(obj.table)) {
    throw new ApiClientError("Malformed bundle response: invalid or missing 'table' object", 502, data);
  }
  const tbl = obj.table as Record<string, unknown>;
  if (typeof tbl.total !== "number" || !Number.isInteger(tbl.total) || tbl.total < 0) {
    throw new ApiClientError("Malformed bundle response: table.total must be a non-negative integer", 502, data);
  }
  if (typeof tbl.page !== "number" || !Number.isInteger(tbl.page) || tbl.page < 1) {
    throw new ApiClientError("Malformed bundle response: table.page must be an integer >= 1", 502, data);
  }
  if (typeof tbl.page_size !== "number" || !Number.isInteger(tbl.page_size) || tbl.page_size < 1) {
    throw new ApiClientError("Malformed bundle response: table.page_size must be an integer >= 1", 502, data);
  }
  if (typeof tbl.total_pages !== "number" || !Number.isInteger(tbl.total_pages) || tbl.total_pages < 0) {
    throw new ApiClientError("Malformed bundle response: table.total_pages must be a non-negative integer", 502, data);
  }
  if (!Array.isArray(tbl.items)) {
    throw new ApiClientError("Malformed bundle response: table.items must be an array", 502, data);
  }

  const validatedItems: RequestRecord[] = tbl.items.map((item, idx) =>
    validateRequestRecord(item, `table.items[${idx}]`)
  );
  const validatedRecentEvents: RequestRecord[] = Array.isArray(obj.recent_security_events)
    ? obj.recent_security_events.map((item, idx) =>
        validateRequestRecord(item, `recent_security_events[${idx}]`)
      )
    : [];

  const rawAttribution = sum.decision_attribution ?? obj.decision_attribution;
  const decisionAttr: BackendDecisionAttribution =
    typeof rawAttribution === "object" && rawAttribution !== null && !Array.isArray(rawAttribution)
      ? {
          crs: typeof (rawAttribution as any).crs === "number" ? Math.max(0, (rawAttribution as any).crs) : 0,
          ai: typeof (rawAttribution as any).ai === "number" ? Math.max(0, (rawAttribution as any).ai) : 0,
          both: typeof (rawAttribution as any).both === "number" ? Math.max(0, (rawAttribution as any).both) : 0,
          unknown: typeof (rawAttribution as any).unknown === "number" ? Math.max(0, (rawAttribution as any).unknown) : 0,
        }
      : { crs: 0, ai: 0, both: 0, unknown: 0 };

  const rawModes = sum.recorded_modes ?? obj.recorded_modes;
  const recordedModes: BackendRecordedModes =
    typeof rawModes === "object" && rawModes !== null && !Array.isArray(rawModes)
      ? {
          shadow: typeof (rawModes as any).shadow === "number" ? Math.max(0, (rawModes as any).shadow) : 0,
          hybrid: typeof (rawModes as any).hybrid === "number" ? Math.max(0, (rawModes as any).hybrid) : 0,
          other: typeof (rawModes as any).other === "number" ? Math.max(0, (rawModes as any).other) : 0,
        }
      : { shadow: 0, hybrid: 0, other: 0 };

  const aiEvalCnt = typeof sum.ai_evaluated_count === "number" ? Math.max(0, sum.ai_evaluated_count) : 0;
  const aiNotEvalCnt = typeof sum.ai_not_evaluated_count === "number" ? Math.max(0, sum.ai_not_evaluated_count) : (sum.total_requests as number);
  const aiSkipCnt = typeof sum.ai_skipped_count === "number" ? Math.max(0, sum.ai_skipped_count) : 0;
  const aiUnrelCnt = typeof sum.ai_unreliable_redaction_count === "number" ? Math.max(0, sum.ai_unreliable_redaction_count) : 0;
  const aiFailCnt = typeof sum.ai_failure_count === "number" ? Math.max(0, sum.ai_failure_count) : 0;
  const aiOtherCnt = typeof sum.ai_other_status_count === "number" ? Math.max(0, sum.ai_other_status_count) : 0;
  const aiCovPct = sum.ai_coverage_percent !== null && sum.ai_coverage_percent !== undefined ? Number(sum.ai_coverage_percent) : null;
  const avgAiRt = sum.avg_ai_roundtrip_ms !== null && sum.avg_ai_roundtrip_ms !== undefined ? Number(sum.avg_ai_roundtrip_ms) : null;

  const aiSummary: BackendAiSummaryMetrics = {
    ai_evaluated_count: aiEvalCnt,
    ai_not_evaluated_count: aiNotEvalCnt,
    ai_skipped_count: aiSkipCnt,
    ai_unreliable_redaction_count: aiUnrelCnt,
    ai_failure_count: aiFailCnt,
    ai_other_status_count: aiOtherCnt,
    ai_coverage_percent: aiCovPct,
    avg_ai_roundtrip_ms: avgAiRt,
  };

  return {
    server_time: obj.server_time as string,
    window: {
      time_range: win.time_range as any,
      time_from: win.time_from as string,
      time_to: win.time_to as string,
    },
    summary: {
      total_requests: sum.total_requests as number,
      block_rate: sum.block_rate !== null && sum.block_rate !== undefined ? Number(sum.block_rate) : null,
      allow_count: sum.allow_count as number,
      flag_count: sum.flag_count as number,
      block_count: sum.block_count as number,
      not_evaluated_count: sum.not_evaluated_count as number,
      avg_transit_ms: sum.avg_transit_ms !== null && sum.avg_transit_ms !== undefined ? Number(sum.avg_transit_ms) : null,
      avg_upstream_header_ms: sum.avg_upstream_header_ms !== null && sum.avg_upstream_header_ms !== undefined ? Number(sum.avg_upstream_header_ms) : null,
      timeouts_count: sum.timeouts_count as number,
      failed_count: sum.failed_count as number,
      ai_evaluated_count: aiEvalCnt,
      ai_not_evaluated_count: aiNotEvalCnt,
      ai_skipped_count: aiSkipCnt,
      ai_unreliable_redaction_count: aiUnrelCnt,
      ai_failure_count: aiFailCnt,
      ai_other_status_count: aiOtherCnt,
      ai_coverage_percent: aiCovPct,
      avg_ai_roundtrip_ms: avgAiRt,
      decision_attribution: decisionAttr,
      recorded_modes: recordedModes,
    },
    sidebar_counts: {
      all: side.all as number,
      blocked: side.blocked as number,
      flagged: side.flagged as number,
      failures: side.failures as number,
    },
    chart: {
      bucket_seconds: chart.bucket_seconds as number,
      buckets: chart.buckets as any,
    },
    table: {
      items: validatedItems,
      total: tbl.total as number,
      page: tbl.page as number,
      page_size: tbl.page_size as number,
      total_pages: tbl.total_pages as number,
    },
    recent_security_events: validatedRecentEvents,
    decision_attribution: decisionAttr,
    recorded_modes: recordedModes,
    ai_summary: aiSummary,
  };
}

/**
 * Internal helper executing bounded fetch with timeout, caller signal merging, and response validation.
 */
async function executeFetch<T>(
  url: string,
  options: RequestOptions = {},
  validator?: (data: unknown) => T
): Promise<T> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const controller = new AbortController();
  let timedOut = false;

  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort(new Error(`Request timed out after ${timeoutMs}ms`));
  }, timeoutMs);

  const onCallerAbort = () => {
    controller.abort(options.signal?.reason);
  };

  if (options.signal) {
    if (options.signal.aborted) {
      clearTimeout(timeoutId);
      throw options.signal.reason ?? new Error("Request was aborted");
    }
    options.signal.addEventListener("abort", onCallerAbort, { once: true });
  }

  const method = options.method ?? "GET";
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...options.headers,
  };

  // Automatically enforce custom CSRF header on state-changing browser mutations
  if (["POST", "PUT", "PATCH", "DELETE"].includes(method) && !headers["X-AIShield-CSRF"]) {
    headers["X-AIShield-CSRF"] = "1";
  }

  let body: BodyInit | undefined = undefined;
  if (options.body !== undefined) {
    if (typeof options.body === "string") {
      body = options.body;
    } else {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(options.body);
    }
  }

  try {
    const response = await fetch(url, {
      method,
      signal: controller.signal,
      headers,
      body,
      credentials: "same-origin",
    });

    let data: unknown;
    const text = await response.text();
    try {
      data = text ? JSON.parse(text) : {};
    } catch (parseErr) {
      throw new ApiClientError(
        `Failed to parse server response as JSON (${response.status})`,
        response.status,
        text
      );
    }

    if (!response.ok) {
      let detailMsg = `HTTP ${response.status} from ${url}`;
      if (data && typeof data === "object" && "detail" in data) {
        detailMsg = String((data as { detail: unknown }).detail);
      } else if (data && typeof data === "object" && "database" in data) {
        detailMsg = `Service unavailable: database ${(data as { database: unknown }).database}`;
      }
      const retryAfterHeader = response.headers.get("retry-after");
      const retryAfterVal = retryAfterHeader ? parseInt(retryAfterHeader, 10) : undefined;
      const retryAfter = typeof retryAfterVal === "number" && !isNaN(retryAfterVal) ? retryAfterVal : undefined;
      throw new ApiClientError(detailMsg, response.status, data, retryAfter);
    }

    if (validator) {
      return validator(data);
    }

    return data as T;
  } catch (err: unknown) {
    if (timedOut) {
      throw new ApiClientError(`Request timeout after ${timeoutMs}ms`, 408);
    }
    if (err instanceof ApiClientError) {
      throw err;
    }
    if (err instanceof Error && err.name === "AbortError") {
      throw err;
    }
    const message = err instanceof Error ? err.message : String(err);
    throw new ApiClientError(message, 0, err);
  } finally {
    clearTimeout(timeoutId);
    if (options.signal) {
      options.signal.removeEventListener("abort", onCallerAbort);
    }
  }
}

/**
 * Health check endpoint: GET /api/v1/healthz
 */
export async function fetchHealthz(
  options: RequestOptions = {}
): Promise<ManagementHealthResponse> {
  const base = options.baseUrl ?? "";
  const url = `${base}/api/v1/healthz`;
  return executeFetch<ManagementHealthResponse>(url, options, validateManagementHealth);
}

/**
 * Dashboard telemetry bundle: GET /api/v1/dashboard-bundle?... or GET /api/v1/demo/dashboard-bundle?...
 */
export async function fetchDashboardBundle(
  query: BundleQueryParams = {},
  options: RequestOptions = {}
): Promise<BackendBundleResponse> {
  const base = options.baseUrl ?? "";
  const params = serializeBundleQueryParams(query);
  const qs = params.toString();
  const endpoint = query.demoEndpoint
    ? "/api/v1/demo/dashboard-bundle"
    : "/api/v1/dashboard-bundle";
  const url = `${base}${endpoint}${qs ? `?${qs}` : ""}`;
  return executeFetch<BackendBundleResponse>(url, options, validateDashboardBundle);
}

/**
 * Request detail endpoint: GET /api/v1/requests/{id} or GET /api/v1/demo/requests/{id}
 */
export async function fetchRequestDetail(
  id: string,
  options: RequestOptions & { websiteId?: string; isDemo?: boolean } = {}
): Promise<RequestRecord> {
  const trimmedId = id.trim();
  if (!UUID_REGEX.test(trimmedId)) {
    throw new ApiClientError(
      `Invalid UUID format for request detail: '${trimmedId}'`,
      422
    );
  }

  const base = options.baseUrl ?? "";
  if (options.isDemo) {
    const url = `${base}/api/v1/demo/requests/${encodeURIComponent(trimmedId)}`;
    return executeFetch<RequestRecord>(url, options, (data) =>
      validateRequestRecord(data, `demo/requests/${trimmedId}`)
    );
  }

  const params = new URLSearchParams();
  if (options.websiteId) {
    params.set("website_id", options.websiteId);
  }
  const qs = params.toString();
  const url = `${base}/api/v1/requests/${encodeURIComponent(trimmedId)}${qs ? `?${qs}` : ""}`;
  return executeFetch<RequestRecord>(url, options, (data) =>
    validateRequestRecord(data, `requests/${trimmedId}`)
  );
}

/**
 * Validates backend AccountResponse structure.
 */
export function validateAccountResponse(data: unknown): AccountResponse {
  if (!data || typeof data !== "object") {
    throw new ApiClientError("Invalid account response format (expected object)", 500, data);
  }
  const obj = data as Record<string, unknown>;
  if (typeof obj.id !== "string" || !UUID_REGEX.test(obj.id)) {
    throw new ApiClientError("Invalid or missing account ID in response", 500, data);
  }
  if (typeof obj.email !== "string" || !obj.email.includes("@")) {
    throw new ApiClientError("Invalid or missing account email in response", 500, data);
  }
  if (typeof obj.status !== "string") {
    throw new ApiClientError("Invalid or missing account status in response", 500, data);
  }
  return {
    id: obj.id,
    email: obj.email,
    status: obj.status,
    created_at: typeof obj.created_at === "string" ? obj.created_at : "",
    updated_at: typeof obj.updated_at === "string" ? obj.updated_at : "",
  };
}

/**
 * Validates LogoutResponse structure.
 */
export function validateLogoutResponse(data: unknown): LogoutResponse {
  if (!data || typeof data !== "object") {
    return { status: "logged_out" };
  }
  const obj = data as Record<string, unknown>;
  return {
    status: typeof obj.status === "string" ? obj.status : "logged_out",
  };
}

/**
 * Customer session check endpoint: GET /api/v1/auth/me
 */
export async function fetchMe(
  options: RequestOptions = {}
): Promise<AccountResponse> {
  const base = options.baseUrl ?? "";
  const url = `${base}/api/v1/auth/me`;
  return executeFetch<AccountResponse>(
    url,
    { ...options, method: "GET" },
    validateAccountResponse
  );
}

/**
 * Customer login endpoint: POST /api/v1/auth/login
 */
export async function loginCustomer(
  payload: LoginPayload,
  options: RequestOptions = {}
): Promise<AccountResponse> {
  const base = options.baseUrl ?? "";
  const url = `${base}/api/v1/auth/login`;
  return executeFetch<AccountResponse>(
    url,
    {
      ...options,
      method: "POST",
      body: payload,
    },
    validateAccountResponse
  );
}

/**
 * Customer registration endpoint: POST /api/v1/auth/register
 */
export async function registerCustomer(
  payload: RegisterPayload,
  options: RequestOptions = {}
): Promise<AccountResponse> {
  const base = options.baseUrl ?? "";
  const url = `${base}/api/v1/auth/register`;
  return executeFetch<AccountResponse>(
    url,
    {
      ...options,
      method: "POST",
      body: payload,
    },
    validateAccountResponse
  );
}

/**
 * Customer logout endpoint: POST /api/v1/auth/logout
 */
export async function logoutCustomer(
  options: RequestOptions = {}
): Promise<LogoutResponse> {
  const base = options.baseUrl ?? "";
  const url = `${base}/api/v1/auth/logout`;
  return executeFetch<LogoutResponse>(
    url,
    {
      ...options,
      method: "POST",
    },
    validateLogoutResponse
  );
}

/**
 * Validates backend WebsiteRecord structure.
 */
export function validateWebsiteRecord(
  data: unknown,
  context: string = "website"
): WebsiteRecord {
  if (!data || typeof data !== "object") {
    throw new ApiClientError(`Invalid ${context} format (expected object)`, 500, data);
  }
  const obj = data as Record<string, unknown>;
  if (typeof obj.id !== "string" || !UUID_REGEX.test(obj.id)) {
    throw new ApiClientError(`Invalid or missing website ID in ${context}`, 500, data);
  }
  if (typeof obj.account_id !== "string" || !UUID_REGEX.test(obj.account_id)) {
    throw new ApiClientError(`Invalid or missing account ID in ${context}`, 500, data);
  }
  if (typeof obj.display_name !== "string" || !obj.display_name.trim()) {
    throw new ApiClientError(`Invalid or missing display_name in ${context}`, 500, data);
  }
  if (typeof obj.public_hostname !== "string" || !obj.public_hostname.trim()) {
    throw new ApiClientError(`Invalid or missing public_hostname in ${context}`, 500, data);
  }
  if (typeof obj.onboarding_status !== "string") {
    throw new ApiClientError(`Invalid or missing onboarding_status in ${context}`, 500, data);
  }

  return {
    id: obj.id,
    account_id: obj.account_id,
    display_name: obj.display_name,
    public_hostname: obj.public_hostname,
    origin_url: typeof obj.origin_url === "string" ? obj.origin_url : "",
    origin_host_header: typeof obj.origin_host_header === "string" ? obj.origin_host_header : null,
    onboarding_status: obj.onboarding_status,
    policy_mode: typeof obj.policy_mode === "string" ? obj.policy_mode : "BLOCKING",
    ai_mode: typeof obj.ai_mode === "string" ? obj.ai_mode : "SHADOW",
    tls_status: typeof obj.tls_status === "string" ? obj.tls_status : "NOT_PROVISIONED",
    is_development: Boolean(obj.is_development),
    verification_token: typeof obj.verification_token === "string" ? obj.verification_token : null,
    dns_txt_record_name: typeof obj.dns_txt_record_name === "string" ? obj.dns_txt_record_name : null,
    created_at: typeof obj.created_at === "string" ? obj.created_at : "",
    updated_at: typeof obj.updated_at === "string" ? obj.updated_at : "",
    verified_at: typeof obj.verified_at === "string" ? obj.verified_at : null,
    activated_at: typeof obj.activated_at === "string" ? obj.activated_at : null,
    dns_instructions:
      obj.dns_instructions && typeof obj.dns_instructions === "object"
        ? {
            record_type: String((obj.dns_instructions as Record<string, unknown>).record_type || "TXT"),
            record_name: String((obj.dns_instructions as Record<string, unknown>).record_name || ""),
            expected_value: String((obj.dns_instructions as Record<string, unknown>).expected_value || ""),
            description: String((obj.dns_instructions as Record<string, unknown>).description || ""),
          }
        : null,
    cname_instructions:
      obj.cname_instructions && typeof obj.cname_instructions === "object"
        ? validateCnameInstructions(obj.cname_instructions)
        : null,
    cname_chain: Array.isArray(obj.cname_chain) ? obj.cname_chain.map(String) : null,
    verification_status: typeof obj.verification_status === "string" ? obj.verification_status : null,
    verification_message: typeof obj.verification_message === "string" ? obj.verification_message : null,
  };
}

/**
 * Validates backend CnameInstructionsSchema structure.
 */
export function validateCnameInstructions(data: unknown): CnameInstructions {
  if (!data || typeof data !== "object") {
    throw new ApiClientError("Invalid CNAME instructions format (expected object)", 500, data);
  }
  const obj = data as Record<string, unknown>;
  return {
    record_type: typeof obj.record_type === "string" ? obj.record_type : "CNAME",
    record_name: typeof obj.record_name === "string" ? obj.record_name : "",
    expected_target: typeof obj.expected_target === "string" ? obj.expected_target : null,
    status: (typeof obj.status === "string" ? obj.status : "INGRESS_NOT_CONFIGURED") as CnameInstructions["status"],
    actionable: Boolean(obj.actionable),
    description: typeof obj.description === "string" ? obj.description : "",
    message: typeof obj.message === "string" ? obj.message : "",
  };
}

/**
 * Validates backend WebsiteListResponse structure.
 */
export function validateWebsiteListResponse(data: unknown): WebsiteListResponse {
  if (!data || typeof data !== "object") {
    throw new ApiClientError("Invalid website list response format", 500, data);
  }
  const obj = data as Record<string, unknown>;
  if (!Array.isArray(obj.items)) {
    throw new ApiClientError("Missing or invalid items array in website list", 500, data);
  }
  const items = obj.items.map((item, idx) => validateWebsiteRecord(item, `website[${idx}]`));
  const total = typeof obj.total === "number" ? obj.total : items.length;
  return { items, total };
}

/**
 * Fetches authenticated account's registered websites: GET /api/v1/websites
 */
export async function fetchWebsites(
  options: RequestOptions = {}
): Promise<WebsiteListResponse> {
  const base = options.baseUrl ?? "";
  const url = `${base}/api/v1/websites`;
  return executeFetch<WebsiteListResponse>(url, options, validateWebsiteListResponse);
}

/**
 * Registers a new customer website: POST /api/v1/websites
 */
export async function createWebsite(
  payload: WebsiteCreatePayload,
  options: RequestOptions = {}
): Promise<WebsiteRecord> {
  const base = options.baseUrl ?? "";
  const url = `${base}/api/v1/websites`;
  return executeFetch<WebsiteRecord>(
    url,
    {
      ...options,
      method: "POST",
      body: {
        display_name: payload.display_name.trim(),
        public_hostname: payload.public_hostname.trim().toLowerCase(),
        origin_url: payload.origin_url.trim(),
      },
    },
    validateWebsiteRecord
  );
}

/**
 * Fetches details for an owned website: GET /api/v1/websites/{id}
 */
export async function fetchWebsite(
  id: string,
  options: RequestOptions = {}
): Promise<WebsiteRecord> {
  const base = options.baseUrl ?? "";
  const url = `${base}/api/v1/websites/${encodeURIComponent(id)}`;
  return executeFetch<WebsiteRecord>(url, options, validateWebsiteRecord);
}

/**
 * Triggers DNS TXT ownership verification: POST /api/v1/websites/{id}/verify-txt
 */
export async function verifyWebsiteTxt(
  id: string,
  options: RequestOptions = {}
): Promise<WebsiteRecord> {
  const base = options.baseUrl ?? "";
  const url = `${base}/api/v1/websites/${encodeURIComponent(id)}/verify-txt`;
  return executeFetch<WebsiteRecord>(
    url,
    {
      ...options,
      method: "POST",
    },
    validateWebsiteRecord
  );
}

/**
 * Fetches DNS CNAME instructions: GET /api/v1/websites/{id}/cname-instructions
 */
export async function fetchCnameInstructions(
  id: string,
  options: RequestOptions = {}
): Promise<CnameInstructions> {
  const base = options.baseUrl ?? "";
  const url = `${base}/api/v1/websites/${encodeURIComponent(id)}/cname-instructions`;
  return executeFetch<CnameInstructions>(url, options, validateCnameInstructions);
}

/**
 * Triggers DNS CNAME routing verification: POST /api/v1/websites/{id}/verify-cname
 */
export async function verifyWebsiteCname(
  id: string,
  options: RequestOptions = {}
): Promise<WebsiteRecord> {
  const base = options.baseUrl ?? "";
  const url = `${base}/api/v1/websites/${encodeURIComponent(id)}/verify-cname`;
  return executeFetch<WebsiteRecord>(
    url,
    {
      ...options,
      method: "POST",
    },
    validateWebsiteRecord
  );
}

